import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { AudioLines, Loader2, Sparkles, Check, ArrowLeft } from 'lucide-react';
import { NotesMarkdown } from './NotesMarkdown';

const escapeHtml = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const textToHtml = (t) => t.split('\n').map(l => `<p>${escapeHtml(l) || '<br>'}</p>`).join('');
const stripHtml = (html) => {
  const d = document.createElement('div');
  d.innerHTML = html || '';
  return (d.innerText || '').trim();
};

export function PlaudImportDialog({ open, onOpenChange, agenda, savedSummaryHtml, onApplied, inboxItem }) {
  const [step, setStep] = useState('input'); // input | review
  const [rawText, setRawText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [general, setGeneral] = useState('');
  const [dealNotes, setDealNotes] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [noTick, setNoTick] = useState({}); // item_id -> true = do NOT mark discussed
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open || !inboxItem) return;
    setRawText(inboxItem.raw_text || '');
    const p = inboxItem.parsed;
    if (p) {
      setGeneral(p.general_summary || '');
      setDealNotes(p.deal_notes || []);
      setUnmatched(p.unmatched || []);
      setNoTick({});
      setStep('review');
    }
  }, [open, inboxItem]);

  const reset = () => {
    setStep('input'); setRawText(''); setGeneral(''); setDealNotes([]); setUnmatched([]); setNoTick({});
  };

  const handleClose = (o) => { if (!o) reset(); onOpenChange(o); };

  const handleParse = async () => {
    if (!rawText.trim()) { toast.error('Paste the Plaud summary first'); return; }
    setParsing(true);
    try {
      const res = await api.parsePlaudSummary(rawText);
      setGeneral(res.data.general_summary || '');
      setDealNotes(res.data.deal_notes || []);
      setUnmatched(res.data.unmatched || []);
      setNoTick({});
      if (!(res.data.deal_notes || []).length && !(res.data.general_summary || '').trim()) {
        toast.info('No content could be mapped — check the pasted text');
      } else {
        setStep('review');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'AI analysis failed'); }
    setParsing(false);
  };

  const handleApply = async () => {
    setApplying(true);
    // Reserve the import first — blocks double-apply when the partner already applied notes for this meeting
    if (inboxItem) {
      try {
        await api.markPlaudApplied(inboxItem.id);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'This import is no longer available');
        reset();
        onOpenChange(false);
        onApplied();
        setApplying(false);
        return;
      }
    }
    try {
      let saved = 0, ticked = 0;
      for (const d of dealNotes) {
        const note = (d.note || '').trim();
        if (!note) continue;
        const item = agenda.find(a => a.id === d.item_id);
        const existing = (item?.notes || '').trim();
        const combined = existing ? `${existing}\n\n${note}` : note;
        if (item && item.status !== 'discussed' && !noTick[d.item_id]) {
          // marks discussed + saves notes + posts "Team Meeting" note on the deal
          await api.discussAgendaItem(d.item_id, combined, textToHtml(combined));
          ticked++;
        } else {
          await api.updateAgendaNotes(d.item_id, combined);
        }
        saved++;
      }
      const gen = general.trim();
      if (gen) {
        const newHtml = textToHtml(gen);
        const combined = stripHtml(savedSummaryHtml) ? `${savedSummaryHtml}<p><br></p>${newHtml}` : newHtml;
        await api.saveMeetingSummaryNote(stripHtml(combined), combined);
      }
      toast.success(`Plaud import applied — ${saved} deal note${saved !== 1 ? 's' : ''}, ${ticked} marked discussed${gen ? ' + Meeting Summary' : ''}`);
      reset();
      onOpenChange(false);
      onApplied();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to apply'); }
    setApplying(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[85vh] flex flex-col" data-testid="plaud-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
            <AudioLines className="w-5 h-5 text-violet-400" /> Plaud Import
            {inboxItem && <span className="text-xs font-normal text-muted-foreground truncate">— {inboxItem.subject}</span>}
          </DialogTitle>
        </DialogHeader>

        {step === 'input' ? (
          <div className="space-y-3 mt-1">
            <p className="text-sm text-muted-foreground">
              Paste the Plaud meeting summary or transcript below. The AI maps deal discussions to the
              agenda items and general topics to the Meeting Summary — you review everything before it is applied.
            </p>
            <textarea value={rawText} onChange={e => setRawText(e.target.value)}
              placeholder="Paste the Plaud summary / transcript here..."
              className="w-full h-64 bg-secondary border border-border rounded-sm p-3 text-sm text-foreground resize-y focus:outline-none focus:border-violet-500 font-mono"
              data-testid="plaud-text-input" autoFocus />
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button variant="ghost" onClick={() => handleClose(false)} className="text-muted-foreground rounded-sm text-xs h-8 px-3">Cancel</Button>
              <Button onClick={handleParse} disabled={parsing || !rawText.trim()} data-testid="plaud-analyze-btn"
                className="bg-violet-600 hover:bg-violet-500 text-white rounded-sm text-xs h-8 px-4">
                {parsing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                {parsing ? 'Analyzing...' : 'Analyze with AI'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4 mt-1 pr-1">
            <p className="text-sm text-muted-foreground">
              Review and edit the extracted notes. On apply, deal notes are saved to the agenda items
              (appended to existing notes), the deals are ticked as <span className="text-emerald-400 font-medium">discussed</span> and
              a "Team Meeting" note is posted on each deal — untick a deal below if it wasn't actually discussed.
              General topics go to the Meeting Summary. Empty fields are skipped.
            </p>

            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Meeting Summary (general topics)</p>
              <textarea value={general} onChange={e => setGeneral(e.target.value)} rows={4}
                placeholder="No general topics found"
                className="w-full bg-secondary border border-border rounded-sm p-3 text-sm text-foreground resize-y focus:outline-none focus:border-violet-500"
                data-testid="plaud-review-general" />
            </div>

            {dealNotes.length > 0 && (
              <div className="space-y-3">
                <p className="text-[10px] text-foreground/40 uppercase tracking-wider">Deal notes ({dealNotes.length})</p>
                {dealNotes.map((d, i) => (
                  <div key={d.item_id} className="border border-border rounded-sm p-3 bg-secondary/30">
                    <p className="text-sm font-medium text-foreground mb-1.5">{d.name}</p>
                    <textarea value={d.note} rows={4}
                      onChange={e => setDealNotes(prev => prev.map((x, j) => j === i ? { ...x, note: e.target.value } : x))}
                      className="w-full bg-secondary border border-border rounded-sm p-2.5 text-sm text-foreground resize-y focus:outline-none focus:border-violet-500"
                      data-testid={`plaud-review-note-${d.item_id}`} />
                    <label className="flex items-center gap-2 mt-1.5 cursor-pointer select-none w-fit"
                      data-testid={`plaud-discuss-tick-${d.item_id}`}>
                      <Checkbox checked={!noTick[d.item_id]}
                        onCheckedChange={(v) => setNoTick(prev => ({ ...prev, [d.item_id]: !v }))}
                        className="border-border data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" />
                      <span className={`text-xs ${noTick[d.item_id] ? 'text-muted-foreground' : 'text-emerald-400'}`}>
                        {noTick[d.item_id] ? 'Only save note — deal stays "to discuss"' : 'Tick as discussed + post note on the deal'}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            )}
            {dealNotes.length === 0 && (
              <p className="text-sm text-muted-foreground italic">No agenda deals were matched in the text.</p>
            )}

            {unmatched.length > 0 && (
              <div className="border border-amber-500/30 bg-amber-500/10 rounded-sm p-3" data-testid="plaud-unmatched">
                <p className="text-xs font-bold text-amber-400 mb-1.5">Not on the agenda — apply manually if needed:</p>
                {unmatched.map((u, i) => (
                  <div key={i} className="text-xs text-amber-300/90 mb-1.5 last:mb-0">
                    <NotesMarkdown text={u} />
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-between gap-2 pt-2 border-t border-border sticky bottom-0 bg-card">
              <Button variant="ghost" onClick={() => setStep('input')} data-testid="plaud-back-btn"
                className="text-muted-foreground rounded-sm text-xs h-8 px-3">
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
              </Button>
              <Button onClick={handleApply} disabled={applying} data-testid="plaud-apply-btn"
                className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm text-xs h-8 px-4">
                {applying ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
                Apply to Agenda
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
